
import { computed, defineComponent, onMounted, reactive } from 'vue'
import moment from 'moment'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import Modal from '@/common/components/UI/Modal/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// module components
import CompanyForm from '@/modules/CompaniesModule/components/CompanyForm/index.vue'
import CompaniesReset from '@/modules/CompaniesModule/components/CompaniesReset/index.vue'
import CompaniesGenerateContract from '@/modules/CompaniesModule/components/CompaniesGenerateContract/index.vue'
import CompaniesPayers from '@/modules/CompaniesModule/components/CompaniesPayers/index.vue'
import CompaniesManagers from '@/modules/CompaniesModule/components/CompaniesManagers/index.vue'
import VerificationEnable from '@/modules/CompaniesModule/components/VerificationEnable/index.vue'
import Billing from '@/modules/CompaniesModule/components/Billing/index.vue'
import CompaniesActions from '@/modules/CompaniesModule/components/CompaniesActions/index.vue'
import ChangeAccountant from '@/modules/CompaniesModule/components/ChangeAccountant/index.vue'
import CompaniesImport from '@/modules/CompaniesModule/components/CompaniesImport/index.vue'
import CompaniesListActions from '@/modules/CompaniesModule/components/CompaniesListActions/index.vue'

// realisations
import { companies } from '@/core/realisations/companies.realisation'
import { billing } from '@/core/realisations/billing.realisation'
import { users } from '@/core/realisations/users.realisation'

// types
import {
    CompanyGenerateContractPayloadT,
    CompanyPayloadT,
    CompanySetClientPayloadT,
    CompanyT,
    CompanyTariff,
    CompanyWorkSchedule,
    CompanySource,
} from '@/core/types/companies.types'

import { ManagerT, UserT } from '@/core/types/users.types'

// classes
import { DataState } from '@/core/classes/DataState.class'

// composable
import useActions from '@/common/composable/useActions'
import useHelpers from '@/common/composable/useHelpers'
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'
import { useFile } from '@/common/composable/useFile'

// validations
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { BillingChangeAccountantPayload } from '@/core/types/billing.types'

// constants
import { CompaniesColumns } from '@/core/constants/columns/Companies.columns'

import sharedFilters from '@/common/composable/useSharedFiltersValues'
const MANAGER_ROLE_ID = 20

const PRIORITY_OPTIONS = [
    { id: 1, name: 'Да', value: '1' },
    { id: 2, name: 'Нет', value: '0' },
]

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        TheInput,
        FailedFetch,
        Modal,
        TheSelect,
        CompanyForm,
        CompaniesReset,
        CompaniesGenerateContract,
        CompaniesPayers,
        CompaniesManagers,
        VerificationEnable,
        CompaniesActions,
        ChangeAccountant,
        CompaniesImport,
        Billing,
        Loader,
        TheTable,
        TableScrollWrapper,
        Cheap,
        CompaniesListActions,
        Checkbox,
    },
    setup() {
        const notifications = useNotifications()
        const companies_copy = reactive({ list: [] as CompanyT[] })
        const account = useAccount()
        const { findItemIndexById } = useHelpers()
        const { saveFile } = useFile()

        const state = reactive({
            companies: new DataState<CompanyT>({}),
            //
            search_query: null as string | null,
            managerIds: [] as number[],
            bin: null as string | null,
            shipmentTariffId: null as number | null,
            billingEnabled: null as number | null,
            createdAtFrom: null as string | null,
            createdAtTo: null as string | null,
            priorityEnabled: null as string | null,
            companySourceIds: [] as number[],
            limit: 50,
        })

        const verification_state = reactive({
            is_opened: false,
            company: null as CompanyT | null,
        })

        const set_client_state = reactive({
            is_opened: false,
            is_loading: false,
            //
            info: null as CompanySetClientPayloadT | null,
        })

        const onStartEnableVerification = (company: CompanyT) => {
            verification_state.company = company
            verification_state.is_opened = true
        }

        const onCancelEnableVerification = () => {
            verification_state.is_opened = false
            verification_state.company = null
        }

        const onVerificationEnabled = (payload: { company_id: number; type_id: number }) => {
            const index = findItemIndexById(payload.company_id, state.companies.list)
            if (index < 0) return

            state.companies.list[index].verify = payload.type_id

            onCancelEnableVerification()
        }

        const onVerificationDisabled = (company_id: number) => {
            const index = findItemIndexById(company_id, state.companies.list)
            if (index < 0) return

            state.companies.list[index].verify = 0
        }

        const loadCompanies = async (page: number) => {
            state.companies.pagination.current_page = page

            try {
                state.companies.is_loading = true

                let priorityEnabled = null
                if (state.priorityEnabled) {
                    priorityEnabled = Number(state.priorityEnabled)
                }

                const params = {
                    page,
                    search: state.search_query || '',
                    bin: state.bin || '',
                    managerIds: state.managerIds,
                    limit: state.limit,
                    createdAtFrom: state.createdAtFrom,
                    createdAtTo: state.createdAtTo,
                    shipmentTariffId: state.shipmentTariffId,
                    billingEnabled: state.billingEnabled,
                    priorityEnabled,
                    companySourceIds: state.companySourceIds
                }

                const data = await companies.getList(params)

                state.companies.list = data.data
                companies_copy.list = data.data

                state.companies.pagination.total_pages = data.meta.last_page
                state.companies.pagination.total_items = data.meta.total

                state.companies.is_failed = false
                state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                state.companies.failure.catchError(error)

                if (!state.companies.failure.error_state.is_cancel) {
                    state.companies.is_failed = true
                }
            } finally {
                if (!state.companies.failure.error_state.is_cancel) {
                    state.companies.is_loading = false
                }
            }
        }

        // MANAGERS
        const loadManagers = async (page: number, search?: string) => {
            data_state.managers.pagination.current_page = page

            try {
                data_state.managers.is_loading = true

                const response = await users.getManagers({ page, name: search, isExtended: 0 })

                data_state.managers.list = response.data
                data_state.managers.pagination.total_pages = response.meta.last_page

                data_state.managers.is_failed = false
                data_state.managers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.managers.failure.catchError(error)

                if (!data_state.managers.failure.error_state.is_cancel) {
                    data_state.managers.is_failed = true
                }
            } finally {
                if (!data_state.managers.failure.error_state.is_cancel) {
                    data_state.managers.is_loading = false
                }
            }
        }

        const loadMoreManagers = async (page: number) => {
            if (page > data_state.managers.pagination.total_pages) return
            data_state.managers.pagination.current_page = page

            try {
                data_state.managers.is_loading = true

                const response = await users.getManagers({ page, isExtended: 0 })
                data_state.managers.list = data_state.managers.list.concat(response.data)

                data_state.managers.pagination.total_pages = response.meta.last_page

                data_state.managers.is_failed = false
                data_state.managers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.managers.failure.catchError(error)

                if (!data_state.managers.failure.error_state.is_cancel) {
                    data_state.managers.is_failed = true
                }
            } finally {
                if (!data_state.managers.failure.error_state.is_cancel) {
                    data_state.managers.is_loading = false
                }
            }
        }

        const searchManagers = async (search_query: string) => {
            await loadManagers(1, search_query)
        }

        const unshiftFoundManager = async (manager: ManagerT) => {
            await loadManagers(data_state.managers.pagination.current_page)
            data_state.managers.list.unshift(manager)
        }

        //
        const loadCompanySources = async () => {
            try {
                data_state.companySources.is_loading = true

                const response = await companies.getCompanySources()
                data_state.companySources.list = response.data

                data_state.companySources.is_failed = false
            } catch (error) {
                console.error(error)
            } finally {
                data_state.companySources.is_loading = false
            }
        }

        //

        const search = () => {
            loadCompanies(state.companies.pagination.current_page)
        }

        const reset = () => {
            state.bin = null
            state.search_query = null
            state.createdAtFrom = null
            state.createdAtTo = null
            state.shipmentTariffId = null
            state.billingEnabled = null
            state.managerIds = []
            state.priorityEnabled = null
            state.companySourceIds = []

            loadCompanies(1)
        }

        // add or update company
        const { actions_state, startCreate, startUpdate } = useActions<CompanyT>()

        const createCompany = async (
            company_payload: CompanyPayloadT & { schedules: CompanyWorkSchedule[] }
        ) => {
            try {
                actions_state.is_actions_loading = true

                await companies.createCompany(company_payload)
                notifications.notify({ title: 'Компания создана', type: 'success' })

                await loadCompanies(1)
                actions_state.is_actions_opened = false
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка при создании компании`,
                })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const updateCompany = async ({ company_payload }: { company_payload: CompanyPayloadT }) => {
            if (!company_payload.id) return

            try {
                actions_state.is_actions_loading = true

                await companies.updateCompany(company_payload.id, company_payload)
                notifications.notify({ title: 'Компания отредактирована', type: 'success' })

                actions_state.is_actions_opened = false
                await loadCompanies(state.companies.pagination.current_page)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка при редактировании компании`,
                })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        // generating contract
        const generate_state = reactive({
            is_loading: false,
            is_opened: false,
            company_id: null as number | null,
        })

        // payers
        const payers_state = reactive({
            is_loading: false,
            is_opened: false,
            company_id: null as number | null,
            payer_company_ids: [] as number[],
        })

        // reset
        const reset_state = reactive({
            is_loading: false,
            is_opened: false,
            email: null as string | null,
        })

        // managers
        const managers_state = reactive({
            is_loading: false,
            is_opened: false,
        })

        // change accountant
        const change_accountant_state = reactive({
            is_loading: false,
            is_opened: false,
        })

        const togglePayers = (company_id?: number, payer_company_ids?: number[]) => {
            payers_state.payer_company_ids = payer_company_ids || []
            payers_state.company_id = company_id || null
            payers_state.is_opened = !payers_state.is_opened
        }

        const onToggleReset = (email: string) => {
            if (!email) return
            reset_state.email = email
            toggleReset()
        }

        const toggleReset = () => {
            reset_state.is_opened = !reset_state.is_opened
        }

        const openGenerateContract = (company_id: number) => {
            generate_state.company_id = company_id
            generate_state.is_opened = true
        }

        const closeGenerateContract = () => {
            generate_state.company_id = null
            generate_state.is_opened = false
        }

        const saveContractBlob = (blob: Blob, file_name: string) => {
            if (!blob) return

            const download_url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')

            link.href = download_url
            link.setAttribute('download', file_name)
            document.body.appendChild(link)

            link.click()
            link.remove()
        }

        const generateContract = async (contract_payload: CompanyGenerateContractPayloadT) => {
            if (!generate_state.company_id) return

            try {
                generate_state.is_loading = true

                const data = await companies.generateContract(
                    generate_state.company_id,
                    contract_payload
                )

                saveContractBlob(data, 'Сгенерированный договор.pdf')

                closeGenerateContract()
            } catch (error) {
                console.error(error)
            } finally {
                generate_state.is_loading = false
            }
        }

        const generateCompanyTranscript = async (company_id: number) => {
            const answer = confirm('Вы действительно хотите сформировать транскрипт?')
            if (!answer) return

            try {
                await billing.generateCompanyTranscript(company_id)

                notifications.notify({
                    title: 'Транскрипт формируется',
                    type: 'success',
                })
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При формировании транскрипта произошла ошибка',
                })
            }
        }

        const handlePayers = async (payer_company_data: number[]) => {
            if (!payers_state.company_id) return

            try {
                payers_state.is_loading = true

                await companies.sendPayers(payers_state.company_id, payer_company_data)
                notifications.notify({
                    title: 'Список плательщиков отправлен',
                    type: 'success',
                })
                togglePayers()
                await loadCompanies(1)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка при отправке списка плательщиков`,
                })
            } finally {
                payers_state.is_loading = false
            }
        }

        const handleReset = async (password: string) => {
            if (!reset_state.email) return

            try {
                reset_state.is_loading = true

                await companies.resetPassword(reset_state.email, password)

                notifications.notify({
                    title: 'Сброс пароля прошел успешно',
                    type: 'success',
                })
                toggleReset()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка при сбросе пароля`,
                })
            } finally {
                reset_state.is_loading = false
            }
        }

        const toggleManagers = () => {
            managers_state.is_opened = !managers_state.is_opened
        }

        const handleManagers = async (form_data: FormData) => {
            try {
                managers_state.is_loading = true

                await companies.changeManagerByExcel(form_data)

                notifications.notify({
                    title: 'Менеджер у всех компаний успешно изменен!',
                    type: 'success',
                })
                toggleManagers()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка при изменении менеджера`,
                })
            } finally {
                managers_state.is_loading = false
            }
        }

        const onSmsActivated = (company_id: number) => {
            const index = findItemIndexById(company_id, state.companies.list)
            if (index < 0) return

            const setting_index = state.companies.list[index].companySettings.findIndex(
                (setting) => setting.key === 'sms'
            )
            if (index < 0) return

            state.companies.list[index].companySettings[setting_index].isActive = true
        }

        const onSmsDisabled = (company_id: number) => {
            const index = findItemIndexById(company_id, state.companies.list)
            if (index < 0) return

            const setting_index = state.companies.list[index].companySettings.findIndex(
                (setting) => setting.key === 'sms'
            )
            if (index < 0) return

            state.companies.list[index].companySettings[setting_index].isActive = false
        }

        const data_state = reactive({
            accountants: new DataState<UserT>({}),
            managers: new DataState<ManagerT>({}),
            tariffs: new DataState<CompanyTariff>({}),
            companySources: new DataState<CompanySource>({})
        })

        const apply_state = reactive({
            is_opened: false,
            is_loading: false,
            //
            accountant_id: null as number | null,
            company_id: null as number | null,
        })

        const apply_form$ = useVuelidate(
            {
                accountant_id: { required },
            },
            apply_state
        )

        const billing_state = reactive({
            is_opened: false,
            //
            company: null as CompanyT | null,
        })

        const loadAccountants = async () => {
            try {
                data_state.accountants.is_loading = true

                const params = {
                    page: 1,
                    roleId: 23,
                }
                const response = await users.getList(params)
                data_state.accountants.list = response.data
                data_state.accountants.pagination.total_pages = response.meta.last_page

                data_state.accountants.is_failed = false
                data_state.accountants.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.accountants.failure.catchError(error)

                if (!data_state.accountants.failure.error_state.is_cancel) {
                    data_state.accountants.is_failed = true
                }
            } finally {
                if (!data_state.accountants.failure.error_state.is_cancel) {
                    data_state.accountants.is_loading = false
                }
            }
        }

        const loadMoreAccountants = async (page: number) => {
            if (page > data_state.accountants.pagination.total_pages) return
            data_state.accountants.pagination.current_page = page

            try {
                data_state.accountants.is_loading = true

                const params = {
                    page: 1,
                    roleId: 23,
                }
                const response = await users.getList(params)
                data_state.accountants.list = data_state.accountants.list.concat(response.data)

                data_state.accountants.pagination.total_pages = response.meta.last_page

                data_state.accountants.is_failed = false
                data_state.accountants.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.accountants.failure.catchError(error)

                if (!data_state.accountants.failure.error_state.is_cancel) {
                    data_state.accountants.is_failed = true
                }
            } finally {
                if (!data_state.accountants.failure.error_state.is_cancel) {
                    data_state.accountants.is_loading = false
                }
            }
        }

        const openApplyModal = (company_id: number) => {
            apply_state.accountant_id = null
            apply_state.company_id = company_id

            apply_form$.value.$reset()

            apply_state.is_opened = true
            loadAccountants()
        }

        const toggleBilling = (company: CompanyT | null) => {
            billing_state.is_opened = Boolean(company)
            billing_state.company = company
        }

        const onSubmitBilling = () => {
            toggleBilling(null)
            loadCompanies(1)
        }

        const closeApplyModal = () => {
            apply_state.is_opened = false
            apply_state.company_id = null
        }

        const applyAccountant = async () => {
            apply_form$.value.$touch()
            if (apply_form$.value.$invalid) return

            try {
                apply_state.is_loading = true

                const accountant_id = apply_state.accountant_id as number
                const company_id = apply_state.company_id as number

                await billing.applyAccountantToCompany(company_id, accountant_id)

                notifications.notify({ title: 'Бухгалтер присвоен', type: 'success' })
                closeApplyModal()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При присвоении бухгалтера произошла ошибка',
                })
            } finally {
                apply_state.is_loading = false
            }
        }

        const setClient = async (payload: CompanySetClientPayloadT) => {
            if (!payload) return

            try {
                set_client_state.is_loading = true

                set_client_state.info = { ...payload }

                await companies.setClient(set_client_state.info as CompanySetClientPayloadT)

                set_client_state.is_opened = false
                notifications.notify({ title: 'Почты клиентов присвоены', type: 'success' })
                loadCompanies(1)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При присвоении почт клиентов произошла ошибка',
                })
            } finally {
                set_client_state.is_loading = false
            }
        }

        const onSetClient = (set_client_info: CompanySetClientPayloadT) => {
            set_client_state.is_opened = true

            set_client_state.info = { ...set_client_info }
        }

        const onLimitChange = (limit: number) => {
            state.limit = limit
            loadCompanies(state.companies.pagination.current_page)
        }

        const onChangePage = (page: number) => {
            loadCompanies(page)
        }

        const loadTariffs = async () => {
            data_state.tariffs.pagination.current_page = 1

            try {
                data_state.tariffs.is_loading = true

                const response = await companies.getTariffs()

                data_state.tariffs.list = response.data
                data_state.tariffs.is_failed = false
                data_state.tariffs.failure.error_state.is_cancel = false
            } catch (error) {
                console.error(error)
                data_state.tariffs.is_failed = true
            } finally {
                data_state.tariffs.is_loading = false
            }
        }

        const openChangeAccountant = () => {
            change_accountant_state.is_opened = true
        }

        const closeChangeAccountant = () => {
            change_accountant_state.is_opened = false
        }

        const changeAccountant = async (payload: BillingChangeAccountantPayload) => {
            try {
                change_accountant_state.is_loading = true

                await billing.changeAccountant(payload)

                notifications.notify({ title: 'Отв. бухгалтер изменен', type: 'success' })
                closeChangeAccountant()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При смене отв. бухгалтера произошла ошибка',
                })
            } finally {
                change_accountant_state.is_loading = false
            }
        }

        const changeIndividualAccountant = async ({
            companyId,
            accountantId,
        }: {
            companyId: number
            accountantId: number
        }) => {
            try {
                change_accountant_state.is_loading = true

                await billing.applyAccountantToCompany(companyId, accountantId)

                notifications.notify({ title: 'Отв. бухгалтер изменен', type: 'success' })
                closeChangeAccountant()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При смене отв. бухгалтера произошла ошибка',
                })
            } finally {
                change_accountant_state.is_loading = false
            }
        }

        const isCanCreateCompany = computed(() => {
            if (account.haveRole(11)) return true
            if (account.havePermission('all-root-permission')) return true

            return false
        })

        const isCanChangeManager = computed(() => {
            if (account.haveRole(11)) return true
            if (account.havePermission('all-root-permission')) return true

            return false
        })

        const isCanChangeAccountant = computed(() => {
            if (account.haveRole(11)) return true
            if (
                account.havePermissions([
                    'company-assign-accountant-by-shipment-tariff',
                    'all-root-permission',
                ])
            )
                return true

            return false
        })

        //

        const exportExcel = async () => {
            try {
                state.companies.is_loading = true

                let priorityEnabled = null
                if (state.priorityEnabled) {
                    priorityEnabled = Number(state.priorityEnabled)
                }

                const params = {
                    search: state.search_query,
                    bin: state.bin,
                    managerIds: state.managerIds,
                    createdAtFrom: state.createdAtFrom,
                    createdAtTo: state.createdAtTo,
                    shipmentTariffId: state.shipmentTariffId,
                    billingEnabled: state.billingEnabled,
                    priorityEnabled,
                    companySourceIds: state.companySourceIds,
                }

                const response = await companies.exportExcel(params)
                saveFile(response, 'Компании')
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка при скачивании`,
                })
            } finally {
                state.companies.is_loading = false
            }
        }

        //

        const import_state = reactive({
            is_opened: false,
        })

        const onImport = () => {
            import_state.is_opened = true
        }

        const onCancelImport = () => {
            import_state.is_opened = false
        }

        const onImported = () => {
            onCancelImport()
            loadCompanies(state.companies.pagination.current_page)
        }

        const getStatusClass = (status_id: number) => {
            let status_class = 'small'

            switch (status_id) {
                case 2:
                    status_class += ' green'
                    break
                case 3:
                    status_class += ' red'
                    break
            }

            return status_class
        }

        onMounted(() => {
            if (account.haveRole(MANAGER_ROLE_ID) && sharedFilters.managerId) {
                state.managerIds = [sharedFilters.managerId]
            }

            loadCompanies(1)
            loadCompanySources()
            loadManagers(1)
            loadTariffs()
        })

        return {
            state,
            billing_state,
            actions_state,
            verification_state,
            generate_state,
            payers_state,
            reset_state,
            managers_state,
            set_client_state,
            change_accountant_state,
            apply_state,
            import_state,
            //
            data_state,
            apply_form$,
            account,
            //
            loadCompanies,
            search,
            reset,
            exportExcel,
            startCreate,
            toggleManagers,
            startUpdate,
            createCompany,
            updateCompany,
            openGenerateContract,
            closeGenerateContract,
            generateContract,
            generateCompanyTranscript,
            handlePayers,
            handleManagers,
            togglePayers,
            toggleReset,
            onToggleReset,
            handleReset,
            onStartEnableVerification,
            onCancelEnableVerification,
            onVerificationEnabled,
            onVerificationDisabled,
            onSmsActivated,
            onSmsDisabled,
            openApplyModal,
            toggleBilling,
            onSubmitBilling,
            closeApplyModal,
            applyAccountant,
            loadTariffs,
            // accountants
            loadAccountants,
            loadMoreAccountants,
            // managers
            loadManagers,
            loadMoreManagers,
            searchManagers,
            unshiftFoundManager,
            //
            setClient,
            onSetClient,
            //
            openChangeAccountant,
            closeChangeAccountant,
            changeAccountant,
            changeIndividualAccountant,
            //
            isCanCreateCompany,
            isCanChangeManager,
            isCanChangeAccountant,
            //
            onImport,
            onCancelImport,
            onImported,
            onLimitChange,
            onChangePage,
            CompaniesColumns,
            getStatusClass,
            moment,
            PRIORITY_OPTIONS,
            loadCompanySources,
        }
    },
})
